import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useTasks } from '../context/TaskContext';

const COLS = ['To Do', 'In Progress', 'Completed'];
const STATUS_COLORS = { 'To Do': '#6366f1', 'In Progress': '#f59e0b', 'Completed': '#10b981' };
const PRIORITY_COLORS = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };
const CAT_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1e2235] border border-gray-700 rounded-lg px-3 py-2 text-xs">
      {label && <p className="text-gray-400 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <span className="font-semibold text-gray-100">{p.value}</span></p>
      ))}
    </div>
  );
};

const ChartCard = ({ title, children, className = '' }) => (
  <div className={`bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-2xl p-5 ${className}`}>
    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">{title}</h3>
    {children}
  </div>
);

const Analytics = () => {
  const { tasks, getDeadlineStatus } = useTasks();
  const total = tasks.length;
  const completed = tasks.filter(t => t.status === 'Completed').length;
  const pending = tasks.filter(t => t.status !== 'Completed').length;
  const overdue = tasks.filter(t => getDeadlineStatus(t.deadline, t.status) === 'overdue').length;
  const progress = total ? Math.round((completed / total) * 100) : 0;

  const byStatus = COLS.map(s => ({ name: s, value: tasks.filter(t => t.status === s).length }));
  const byPriority = ['High', 'Medium', 'Low'].map(p => ({ name: p, value: tasks.filter(t => t.priority === p).length }));
  const byCategory = ['Work', 'Study', 'Personal', 'Other'].map((c, i) => ({ name: c, value: tasks.filter(t => t.category === c).length, fill: CAT_COLORS[i] })).filter(d => d.value > 0);

  const RADIAN = Math.PI / 180;
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value }) => {
    if (value === 0) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600}>{value}</text>;
  };

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: total, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
          { label: 'Completed', value: completed, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          { label: 'Pending', value: pending, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Overdue', value: overdue, color: 'text-red-400', bg: 'bg-red-500/10' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-4 border border-gray-200 dark:border-transparent`}>
            <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-2xl p-5">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Overall Completion</h3>
          <span className="text-sm font-bold text-indigo-400">{progress}%</span>
        </div>
        <div className="w-full h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-400 rounded-full transition-all duration-1000" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-xs text-gray-400">{completed} of {total} tasks done</span>
          <span className="text-xs text-gray-400">{pending} remaining</span>
        </div>
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ChartCard title="Tasks by Status">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={byStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" labelLine={false} label={renderCustomLabel}>
                {byStatus.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.name]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend formatter={(v) => <span style={{ fontSize: 11, color: '#9fa3c0' }}>{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tasks by Priority">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byPriority} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b6f8a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6b6f8a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} name="Tasks">
                {byPriority.map((e, i) => <Cell key={i} fill={PRIORITY_COLORS[e.name]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tasks by Category">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={byCategory} cx="50%" cy="50%" outerRadius={85} dataKey="value" labelLine={false} label={renderCustomLabel}>
                {byCategory.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend formatter={(v) => <span style={{ fontSize: 11, color: '#9fa3c0' }}>{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Task Breakdown">
          <div className="space-y-3 pt-1">
            {[
              { label: 'To Do', value: byStatus[0].value, total, color: '#6366f1' },
              { label: 'In Progress', value: byStatus[1].value, total, color: '#f59e0b' },
              { label: 'Completed', value: byStatus[2].value, total, color: '#10b981' },
              { label: 'High Priority', value: byPriority[0].value, total, color: '#ef4444' },
              { label: 'Overdue', value: overdue, total, color: '#ef4444' },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{label}</span>
                  <span>{value} ({total ? Math.round((value / total) * 100) : 0}%)</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${total ? (value / total) * 100 : 0}%`, background: color }} />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default Analytics;
